import { PIPELINE } from '../../shared/domain';

export const DEFAULT_SHOP_SETTINGS = {
  version: 1,
  business: {
    companyName: 'REKON',
    shopName: 'REKON',
    shopId: 'DGP-ALBANY',
    timezone: 'America/New_York',
    // Remit-to block printed on every estimate and invoice. Without these the
    // customer-facing paper has no address to mail a check to.
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    phone: '',
    email: '',
    website: '',
  },
  branding: {
    appName: 'REKON',
    logoKey: null,
    logoUrl: null,
    logoBase64: null,
    primaryColor: '#0f172a',
    accentColor: '#f59e0b',
    successColor: '#22c55e',
    dangerColor: '#dc2626',
  },
  flow: {
    columns: PIPELINE.map((stage, i) => ({
      id: stage.id,
      label: stage.label,
      visible: true,
      order: i,
    })),
  },
  timers: {
    useBusinessHours: false,
    stageThresholds: {},
  },
  priority: {
    customerTypeWeights: {
      SOLD: 65,
      REGULAR: 60,
      FLEET: 20,
      WALKIN: 0,
    },
    hardDownBoost: 20,
    overdueBoost: 5,
    idleBoost: 2,
  },
  commandCenter: {
    widgets: [
      { id: 'shopHealth', label: 'Shop Health', visible: true, order: 0 },
      { id: 'slaRisk', label: 'SLA Risk', visible: true, order: 1 },
      { id: 'activePipeline', label: 'Active Pipeline', visible: true, order: 2 },
      { id: 'criticalUnits', label: 'Critical Units', visible: true, order: 3 },
    ],
  },
  archive: {
    keepFullRecordsForever: true,
    keepPhotosForever: true,
    summarizeAfterMonths: 24,
    allowAutomaticDeletion: false,
  },
  pricing: {
    defaultLaborRate: 95,
    taxRate: 0,
    partsMarkup: 0,
    paymentTermsDays: 30,

    // ── Road service (portal to portal) ──────────────────────────────
    // Defaults are deliberately inert: a shop that never opens this screen
    // bills road calls exactly as it did before the feature existed.
    // roadServiceLaborRate 0 falls back to defaultLaborRate; a 0 mileage rate
    // or trip charge emits no line at all rather than a $0 one.
    roadServiceLaborRate: 0,
    roadServiceMileageRate: 0,
    roadServiceTripCharge: 0,
    roadServiceMinimumHours: 0,
    roadServiceRoundingIncrement: 0.25,

    // Rounding for bench labor billed off the tech clock. Same rule: shops
    // bill to the quarter hour and round up.
    laborRoundingIncrement: 0.25,
    invoiceDisclaimer:
      'All parts and service sales are final. Payment is due upon receipt unless payment terms are stated above. '
      + 'We are not responsible for loss or damage to vehicles, or articles left in vehicles, from fire, theft, '
      + 'weather, or any cause beyond our control. Storage fees may apply to vehicles left more than 3 days after '
      + 'completion of repairs.',
  },
  // How REKON's invoice lines land in the shop's chart of accounts. `items`
  // are QuickBooks Product/Service names (Online CSV + Desktop IIF); `accounts`
  // are Desktop account names, only used by the IIF export.
  accounting: {
    quickbooksFormat: 'csv', // csv = QuickBooks Online, iif = Desktop/Enterprise
    // How sales tax rides along on a QuickBooks Online CSV import.
    //   'quickbooks' — code each line TAX/NON and let Automated Sales Tax do
    //                  the math. Correct for nearly every US QBO company; a
    //                  tax line item on top of AST would double the tax.
    //   'lineitem'   — send our own "Sales Tax" line. Only for companies not
    //                  on Automated Sales Tax.
    qboTaxMode: 'quickbooks',
    items: {
      labor: 'Labor',
      parts: 'Parts',
      shopSupplies: 'Shop Supplies',
      tax: 'Sales Tax',
    },
    accounts: {
      receivable: 'Accounts Receivable',
      labor: 'Labor Income',
      parts: 'Parts Income',
      shopSupplies: 'Parts Income',
      tax: 'Sales Tax Payable',
    },
  },
  catalog: {
    categories: [
      'Preventive Maintenance', 'Brakes', 'Engine', 'Electrical',
      'HVAC', 'Tires', 'Suspension', 'DOT / Inspection', 'Diagnostics', 'General',
    ],
    items: [],
  },
  // null = legacy shop (full access); set by Stripe webhook via /api/stripe-webhook.
  // Shape once set: { status, plan: 'flow'|'ops'|'full', roadServiceSku: Boolean,
  // stripeCustomerId, stripeSubscriptionId, currentPeriodEnd }. roadServiceSku is
  // an add-on, independent of plan tier — see src/platform/engine/entitlements.js.
  subscription: null,
};
